
// =============================================================
// SMART DROP ENGINE — local, no-LLM product & brand recognition.
// =============================================================
// No external vision/LLM API is available, so this "engine" recognises a
// product and its brand entirely client-side from two signals the admin gives:
//   1. A product photo (used as the drop's brand/product visual).
//   2. The product's website URL (the strongest structured signal — the domain
//      reliably encodes the brand, and the path/keywords hint at the product).
//
// It derives a believable, on-brand DROP draft (title, brand, campaign,
// description, socials, a product entry and starter questions) that the admin
// reviews and edits before creating the drop. Results are DETERMINISTIC for the
// same inputs so the same website always resolves to the same brand draft.
// =============================================================

import { brandMeta, buildBrandQuestions } from './seed'
import type { DropQuestion, DropProduct } from './store'

export interface RecognitionResult {
  brand: string
  /** Suggested product/drop title. */
  title: string
  campaign: string
  description: string
  brandLogo: string
  instagramUrl: string
  websiteUrl: string
  products: DropProduct[]
  questions: DropQuestion[]
  /** 0–100 heuristic confidence for the admin to gauge the match. */
  confidence: number
  /** Short, human-readable notes on how the guess was made. */
  signals: string[]
}

/** Deterministic small hash from a string → non-negative int. */
const seedFrom = (s: string): number => {
  let h = 2166136261
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return Math.abs(h)
}

const pick = <T,>(arr: T[], seed: number, salt = 0): T =>
  arr[(seed + salt) % arr.length]

const titleCase = (s: string) =>
  s
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')

/** Normalise a raw URL/host string into { host, brand, url } best-effort. */
export function parseWebsite(raw: string): {
  host: string
  brand: string
  url: string
  path: string
} {
  const trimmed = (raw || '').trim()
  if (!trimmed) return { host: '', brand: '', url: '', path: '' }
  let url = trimmed
  if (!/^https?:\/\//i.test(url)) url = `https://${url}`
  let host = ''
  let path = ''
  try {
    const u = new URL(url)
    host = u.hostname.replace(/^www\./i, '')
    path = u.pathname || ''
  } catch {
    host = trimmed
      .replace(/^https?:\/\//i, '')
      .replace(/^www\./i, '')
      .split('/')[0]
  }
  // Brand = the domain's second-level label, e.g. "vitaminwell.com" → "vitaminwell".
  const label = host.split('.').filter(Boolean)[0] || host
  // Split camelCase / separators into words for a readable brand name.
  const words = label
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[-_]+/g, ' ')
    .trim()
  return { host, brand: titleCase(words || label), url, path }
}

const CAMPAIGN_THEMES = [
  'Fresh Drop',
  'Tasting Launch',
  'Sampling Pop-Up',
  'New Arrival',
  'Discovery Drop',
  'Limited Tasting',
]

const DESCRIPTORS = [
  'a fan-favourite',
  'a best-selling',
  'a brand-new',
  'a limited-edition',
  'a cult-classic',
]

/** Guess a product-type word from the URL path/keywords (kept simple, no LLM). */
const guessProductType = (path: string): string => {
  const p = path.toLowerCase()
  const map: [RegExp, string][] = [
    [/drink|soda|water|energy|beverage|cola|juice/, 'drink'],
    [/bar|protein|snack|cookie|chip|candy|choc/, 'snack'],
    [/coffee|espresso|brew|roast/, 'coffee'],
    [/tea|matcha/, 'tea'],
    [/care|beauty|skin|cosmet/, 'product'],
    [/supplement|vitamin|health/, 'supplement'],
  ]
  for (const [re, word] of map) if (re.test(p)) return word
  return 'product'
}

/**
 * Recognise a product + brand from an uploaded image and/or a website URL and
 * return a ready-to-edit drop draft. Purely local & deterministic.
 */
export function recognizeProduct(input: {
  imageDataUrl?: string
  website?: string
}): RecognitionResult {
  const { imageDataUrl, website } = input
  const site = parseWebsite(website || '')
  const brand = site.brand || 'Unknown Brand'
  const seed = seedFrom(`${site.host}|${brand}`)

  const meta = brandMeta(brand)
  const productType = guessProductType(site.path)
  const descriptor = pick(DESCRIPTORS, seed, 1)
  const campaign = pick(CAMPAIGN_THEMES, seed, 2)

  const title = `${brand} ${titleCase(productType)} Drop`
  const description = `Try ${descriptor} ${productType} from ${brand}. ${
    site.host ? `Sourced from ${site.host}, ` : ''
  }freshly dropped for a limited tasting — grab a box, taste it, and tell us what you think.`

  // Prefer the admin's uploaded photo as the visual; fall back to the derived logo.
  const brandLogo = imageDataUrl || meta.brandLogo

  const products: DropProduct[] = [
    {
      name: `${brand} ${titleCase(productType)}`,
      brand,
      logo: brandLogo,
    },
  ]

  const questions = buildBrandQuestions(brand, 3, productType)

  // Confidence: website domain is the strong signal; the photo adds a little.
  let confidence = 0
  const signals: string[] = []
  if (site.host) {
    confidence += 68
    signals.push(`Brand "${brand}" read from domain ${site.host}`)
  }
  if (site.path && site.path.length > 1) {
    confidence += 12
    signals.push(`Product type "${productType}" inferred from the URL path`)
  } else if (site.host) {
    signals.push(`Product type defaulted to "${productType}"`)
  }
  if (imageDataUrl) {
    confidence += 18
    signals.push('Uploaded photo attached as the drop visual')
  }
  if (!site.host && !imageDataUrl) {
    signals.push('No website or image provided — nothing to recognise yet')
  }
  confidence = Math.min(99, confidence)

  return {
    brand,
    title,
    campaign,
    description,
    brandLogo,
    instagramUrl: meta.instagramUrl,
    websiteUrl: site.url || meta.websiteUrl,
    products,
    questions,
    confidence,
    signals,
  }
}
